/*
 * Copyright (c) 2017-present, flop! contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 * https://framagit.org/flopedt/flopedt
 */

import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'

import { computed } from 'vue'
import { useRoute } from 'vue-router'

import {
  createDepartment,
  deleteDepartment,
  getAllDepartments,
  getDepartment,
  updateDepartment,
} from '@/ts/api/departments.ts'
import { defineCrudStore } from '../defineCrudStore.ts'

export const useDepartmentsStore = defineCrudStore({
  name: 'departments',
  fetchAll: getAllDepartments,
  fetchOne: getDepartment,
  create: createDepartment,
  update: updateDepartment,
  remove: deleteDepartment,
  setup({ entities }) {
    const route = useRoute()
    const currentDepartmentAbbrev = computed(() => route.params.dept as string)
    const isCurrentDepartmentSelected = computed(() => !!currentDepartmentAbbrev.value)

    const current = computed(
      // TYPE SAFETY: This expression is asserted, which weakens type safety.
      // This is done for convenience, and most likely safe since in practice it should never be undefined wherever used.
      // The navigation will wait for the store to have loaded all the available departments and will reject the
      // navigation to a nonexistent department. The only other case is if this is used in a non-department route, which
      // is not something that makes sense (so undefined behavior is expected).
      () => entities.find((dept) => dept.abbrev === currentDepartmentAbbrev.value)!
    )

    return {
      current,
      currentDepartmentAbbrev,
      isCurrentDepartmentSelected,
    }
  },
})

// https://router.vuejs.org/guide/advanced/navigation-guards.html#Per-Route-Guard
export function handleRouteDepartment(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const departments = useDepartmentsStore()
  if (!departments.entities.find((dept) => dept.abbrev === to.params.dept)) {
    // Department does not exist, go to index
    return next({ path: '/' })
  }

  return next()
}
